import Config from '../../config';
import { loadPartnerDetails as loadPartnerDetailsJs } from './supplier_onboarding';
import { ShippingLabelStatus } from './shippingLabelStatus';

export interface PartnerTasks {
    inventoryEnabled: boolean;
    inventoryPosted: boolean;
    productEnabled: boolean;
    productPosted: boolean;
    productPoc: boolean;
    testingEnabled: boolean;
    testingComplete: boolean;
    connectionConfigured: boolean;
    integrationType: string;
    connectionPrimary?: string | null;
    connectionLink?: string | null;
    // Come from the retailer config and integration row rather than the JS
    // loader, so they are merged in after the shape guard below.
    shippingLabelEnabled?: boolean;
    shippingLabelStatus?: string | null;
}

/**
 * Runtime guard catching JS-side shape drift. If `supplier_onboarding.js` ever
 * drops or renames a flag without updating the TS wrapper, this returns false
 * and the modal treats the row as "unevaluated" instead of silently
 * misclassifying it.
 */
export function isPartnerTasksLike(value: unknown): value is PartnerTasks {
    if (!value || typeof value !== 'object') return false;
    const t = value as Record<string, unknown>;
    return (
        typeof t.inventoryEnabled === 'boolean' &&
        typeof t.inventoryPosted === 'boolean' &&
        typeof t.productEnabled === 'boolean' &&
        typeof t.productPosted === 'boolean' &&
        typeof t.productPoc === 'boolean' &&
        typeof t.testingEnabled === 'boolean' &&
        typeof t.testingComplete === 'boolean' &&
        typeof t.connectionConfigured === 'boolean' &&
        typeof t.integrationType === 'string'
    );
}

export async function loadPartnerTasks(integration: {
    CoId: number | string;
    IntegrationType?: string;
    ShippingLabelEnabled?: boolean;
    ShippingLabelStatus?: string | null;
}): Promise<PartnerTasks | null> {
    const raw = await loadPartnerDetailsJs(integration);
    if (!isPartnerTasksLike(raw)) return null;
    return {
        ...raw,
        shippingLabelEnabled: Boolean(integration.ShippingLabelEnabled),
        shippingLabelStatus: integration.ShippingLabelStatus ?? null,
    };
}

/**
 * Returns true iff every enabled flag has its corresponding *posted/*Configured/*Complete true.
 * In production the testing task is hidden from the UI (`supplier_onboarding.js:354`);
 * the predicate mirrors that visibility rule.
 */
export function isAllTasksComplete(tasks: PartnerTasks): boolean {
    if (tasks.inventoryEnabled && !tasks.inventoryPosted) return false;
    if (tasks.productEnabled && !tasks.productPosted) return false;
    if (!Config.production && tasks.testingEnabled && !tasks.testingComplete) return false;
    if (tasks.connectionPrimary && !tasks.connectionConfigured) return false;
    if (tasks.shippingLabelEnabled && tasks.shippingLabelStatus !== ShippingLabelStatus.Complete) return false;
    return true;
}
